// crewbit-admin/src/hooks/useTeamUtilization.ts
//
// Schedule Module Phase C C5B — Team scoreboard aggregation hook (admin-only;
// mobile drops Team per Foundation v1 §F3).
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §6.
//
// Aggregates per-resource booked + available hours + top-3 upcoming entries
// for the week range. Composes existing useScheduleEntries(range='week') with
// a direct schedule_shifts read keyed off (business_id, date BETWEEN). Uses
// live shifts shape (date + start_time + end_time, NOT start_at/end_at).
//
// Direct SELECT on schedule_shifts is INV-Schedule-clean — schedule_shifts is
// in BANNED_MUTATION but reads are unrestricted (only mutations route through
// callScheduleRpc).
//
// Phase C scope: client-side aggregation. If real-tenant data shows >1s
// aggregation time on 50+ operator businesses, file
// §Schedule.TeamUtilizationServerSideAgg for Phase D substrate session.

import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useScheduleEntries } from './useScheduleEntries';
import type { ScheduleEntry } from '../types';
import type { ScheduleUser } from '../components/schedule/scheduleHelpers';

export interface TeamUtilizationRow {
  bookedMinutes: number;
  availableMinutes: number;
  /** booked / available, capped at 1.0 unless overbooked. NaN if available is 0 (no shift scheduled). */
  utilization: number | null;
  upcoming: ScheduleEntry[];
}

export interface UseTeamUtilizationResult {
  rows: Map<string, TeamUtilizationRow>;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

interface ShiftRow {
  user_id: string;
  date: string;
  start_time: string;
  end_time: string;
}

function timeStringToMinutes(t: string): number {
  // "HH:MM:SS" or "HH:MM" → minutes since midnight
  const [hh, mm] = t.split(':').map(Number);
  return (hh ?? 0) * 60 + (mm ?? 0);
}

function isoDateRange(weekStart: Date, weekEnd: Date): { startDate: string; endDate: string } {
  const fmt = (d: Date) => {
    const yyyy = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mo}-${dd}`;
  };
  return { startDate: fmt(weekStart), endDate: fmt(weekEnd) };
}

export function useTeamUtilization(
  businessId: string | undefined,
  weekStart: Date,
  weekEnd: Date,
  resources: ScheduleUser[],
): UseTeamUtilizationResult {
  // useScheduleEntries already supports range='week'; pass any date in the week.
  const { entries, loading: entriesLoading, error: entriesError, refresh: refreshEntries } =
    useScheduleEntries(businessId, weekStart, 'week');

  // Shifts state held in a single object so the consumer-facing loading flag
  // is derived (no setState-in-effect dance the React 19 hooks lint rejects).
  const [shiftsState, setShiftsState] = useState<{ rows: ShiftRow[]; ready: boolean; error: string | null }>(
    { rows: [], ready: false, error: null },
  );
  const mountedRef = useRef(true);
  const setShiftsStateRef = useRef(setShiftsState);
  useEffect(() => { setShiftsStateRef.current = setShiftsState; }, []);

  const { startDate, endDate } = useMemo(
    () => isoDateRange(weekStart, weekEnd),
    [weekStart, weekEnd],
  );

  useEffect(() => {
    mountedRef.current = true;
    if (!businessId) return;
    let cancelled = false;
    const run = async () => {
      const { data, error } = await supabase
        .from('schedule_shifts')
        .select('user_id, date, start_time, end_time')
        .eq('business_id', businessId)
        .gte('date', startDate)
        .lte('date', endDate);
      if (cancelled || !mountedRef.current) return;
      setShiftsStateRef.current({
        rows: error ? [] : (data ?? []) as ShiftRow[],
        ready: true,
        error: error?.message ?? null,
      });
    };
    void run();
    return () => { cancelled = true; mountedRef.current = false; };
  }, [businessId, startDate, endDate]);

  const rows = useMemo<Map<string, TeamUtilizationRow>>(() => {
    const map = new Map<string, TeamUtilizationRow>();
    const now = new Date();

    for (const r of resources) {
      // Booked: sum of (end_at - start_at) of entries assigned to this resource
      const myEntries = entries.filter((e) =>
        Array.isArray(e.assigned_to) && e.assigned_to.includes(r.id),
      );
      const bookedMinutes = myEntries.reduce((acc, e) => {
        const startMs = new Date(e.start_at).getTime();
        const endMs = new Date(e.end_at).getTime();
        if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return acc;
        return acc + Math.round((endMs - startMs) / 60000);
      }, 0);

      // Available: sum of (end_time - start_time) per shift for this user
      const myShifts = shiftsState.rows.filter((s) => s.user_id === r.id);
      const availableMinutes = myShifts.reduce((acc, s) => {
        const startMin = timeStringToMinutes(s.start_time);
        const endMin = timeStringToMinutes(s.end_time);
        if (endMin <= startMin) return acc;
        return acc + (endMin - startMin);
      }, 0);

      // Upcoming top-3 (not started yet)
      const upcoming = myEntries
        .filter((e) => new Date(e.start_at).getTime() > now.getTime())
        .sort((a, b) => a.start_at.localeCompare(b.start_at))
        .slice(0, 3);

      const utilization = availableMinutes > 0 ? bookedMinutes / availableMinutes : null;
      map.set(r.id, { bookedMinutes, availableMinutes, utilization, upcoming });
    }

    return map;
  }, [resources, entries, shiftsState.rows]);

  return {
    rows,
    loading: entriesLoading || (Boolean(businessId) && !shiftsState.ready),
    error: entriesError ?? shiftsState.error,
    refresh: refreshEntries,
  };
}
